import {
  ENTRA_CARD_FIELDS,
  ENTRA_FIELD_SOURCE,
  extractLiquidVars,
  isFormField,
  LIQUID_VIRTUAL_FIELDS,
  parseFieldMarker,
} from '@tronche2tek/shared-types';

/** How a default is sourced: a fixed string, a source marker, or filled at generation. */
export type FieldMode = 'fixed' | 'entra' | 'external' | 'form';

/**
 * The external source a template's type binds to — ISEE for student templates,
 * Lucca for employee ones — with the fields it offers. `null` for a template
 * type that has none, which is a distinction the blank-Fixed rule depends on:
 * 'other'/CSV templates leave Fixed fields empty on purpose, since the value
 * comes from a CSV column. An empty `fields` means the source exists but its
 * list hasn't loaded (or the integration is off), so it cannot be offered yet.
 */
export interface ExternalSource {
  prefix: string;
  fields: { key: string }[];
}

/**
 * The editor's fields, both keyed by template variable name. `values` holds
 * what gets persisted (a plain string or a `<source>:<field>` marker); `modes`
 * is the picker above each one and never leaves the form.
 */
export interface DefaultsFields {
  values: Record<string, string>;
  modes: Record<string, FieldMode>;
}

/** The template's Liquid variables, bar the two the renderer injects. */
export function configurableVars(meta: {
  frontSvg: string;
  backSvg?: string | null;
}): string[] {
  const all = new Set<string>([
    ...extractLiquidVars(meta.frontSvg),
    ...(meta.backSvg ? extractLiquidVars(meta.backSvg) : []),
  ]);
  return [...all].filter((v) => !LIQUID_VIRTUAL_FIELDS.has(v)).sort();
}

/**
 * The form's starting state for a template: each variable's stored default,
 * plus the mode that default implies. A marker bound to a source other than
 * this template's own external one reads as fixed.
 */
export function seedFields(
  vars: string[],
  defaults: Record<string, string> | null | undefined,
  external: ExternalSource | null,
): DefaultsFields {
  const prefix = external?.prefix ?? '';
  return {
    values: Object.fromEntries(vars.map((v) => [v, defaults?.[v] ?? ''])),
    modes: Object.fromEntries(
      vars.map((v) => {
        const stored = defaults?.[v] ?? '';
        const marker = parseFieldMarker(stored);
        const mode: FieldMode = isFormField(stored)
          ? 'form'
          : marker?.source === ENTRA_FIELD_SOURCE
            ? 'entra'
            : marker && prefix && marker.source === prefix
              ? 'external'
              : 'fixed';
        return [v, mode];
      }),
    ),
  };
}

/**
 * An incomplete field is a mistake: a source mode with no field picked, or a
 * Fixed field left blank on a template that has no CSV to fall back on. The
 * save is blocked until it's resolved.
 */
export function incompleteIn(
  vars: string[],
  fields: DefaultsFields,
  external: ExternalSource | null,
): string[] {
  return vars.filter((v) => {
    const mode = fields.modes[v] ?? 'fixed';
    const blank = (fields.values[v] ?? '').trim() === '';
    if (mode === 'form') {
      return false;
    }
    if (mode === 'fixed') {
      return external != null && blank;
    }
    return blank;
  });
}

/** The marker prefix a mode writes, or '' for the modes that write none. */
export function sourceOf(
  mode: FieldMode,
  external: ExternalSource | null,
): string {
  if (mode === 'entra') {
    return ENTRA_FIELD_SOURCE;
  }
  return mode === 'external' ? (external?.prefix ?? '') : '';
}

/** The field names a source mode offers. */
export function fieldsFor(
  mode: FieldMode,
  external: ExternalSource | null,
): readonly string[] {
  if (mode === 'entra') {
    return ENTRA_CARD_FIELDS;
  }
  return external?.fields.map((f) => f.key) ?? [];
}

/** The field this stored value binds to under `mode`, or '' if it doesn't. */
export function boundField(
  mode: FieldMode,
  value: string,
  external: ExternalSource | null,
): string {
  const marker = parseFieldMarker(value);
  const src = sourceOf(mode, external);
  return marker && src && marker.source === src ? marker.field : '';
}

/**
 * Whether a value survives a switch to `mode`: a source mode keeps it only if
 * it already binds to that source, 'fixed' only if it is a plain string.
 */
export function keepsValue(
  mode: FieldMode,
  value: string,
  external: ExternalSource | null,
): boolean {
  return mode === 'fixed'
    ? !parseFieldMarker(value) && !isFormField(value)
    : boundField(mode, value, external) !== '';
}
